import { PolicyType, PolicyRole, Constraint } from '@htx/policies';

export interface ConstraintTypeOption {
  type: PolicyType;
  labelKey: string;
  descriptionKey: string;
  icon: string;
  category: 'access' | 'time' | 'region' | 'compliance';
  allowedIn: PolicyRole[];
}

export interface ConstraintCategory {
  id: 'access' | 'time' | 'region' | 'compliance';
  labelKey: string;
  icon: string;
  color: string;
  types: ConstraintTypeOption[];
}

export const CATEGORY_COLORS: Record<string, string> = {
  access: '#165b98',
  time: '#2cb5a8',
  region: '#8b5cf6',
  compliance: '#d97706'
};

/** All addable constraint types, organized by category. */
export const CONSTRAINT_CATEGORIES: ConstraintCategory[] = [
  {
    id: 'access',
    labelKey: 'policyBuilder.category.access',
    icon: 'lock',
    color: CATEGORY_COLORS['access'],
    types: [
      {
        type: PolicyType.PARTICIPANT_RESTRICTED,
        labelKey: 'policyBuilder.type.participant.label',
        descriptionKey: 'policyBuilder.type.participant.description',
        icon: 'people',
        category: 'access',
        allowedIn: ['access']
      },
      {
        type: PolicyType.MEMBERSHIP_STATIC,
        labelKey: 'policyBuilder.type.membership.label',
        descriptionKey: 'policyBuilder.type.membership.description',
        icon: 'verified_user',
        category: 'access',
        allowedIn: ['access', 'usage']
      },
      {
        type: PolicyType.USE_CASE_MEMBERSHIP,
        labelKey: 'policyBuilder.type.useCase.label',
        descriptionKey: 'policyBuilder.type.useCase.description',
        icon: 'cases',
        category: 'access',
        allowedIn: ['usage']
      }
    ]
  },
  {
    id: 'time',
    labelKey: 'policyBuilder.category.time',
    icon: 'schedule',
    color: CATEGORY_COLORS['time'],
    types: [
      {
        type: PolicyType.TIME_PERIOD_RESTRICTED,
        labelKey: 'policyBuilder.type.timePeriod.label',
        descriptionKey: 'policyBuilder.type.timePeriod.description',
        icon: 'hourglass_empty',
        category: 'time',
        allowedIn: ['usage']
      },
      {
        type: PolicyType.END_DATE,
        labelKey: 'policyBuilder.type.endDate.label',
        descriptionKey: 'policyBuilder.type.endDate.description',
        icon: 'event',
        category: 'time',
        allowedIn: ['usage']
      }
    ]
  },
  {
    id: 'region',
    labelKey: 'policyBuilder.category.region',
    icon: 'public',
    color: CATEGORY_COLORS['region'],
    types: [
      {
        type: PolicyType.COMPANY_REGION_RESTRICTED,
        labelKey: 'policyBuilder.type.companyRegion.label',
        descriptionKey: 'policyBuilder.type.companyRegion.description',
        icon: 'domain',
        category: 'region',
        allowedIn: ['usage']
      },
      {
        type: PolicyType.DATACENTER_REGION_RESTRICTED,
        labelKey: 'policyBuilder.type.datacenterRegion.label',
        descriptionKey: 'policyBuilder.type.datacenterRegion.description',
        icon: 'dns',
        category: 'region',
        allowedIn: ['usage']
      }
    ]
  },
  {
    id: 'compliance',
    labelKey: 'policyBuilder.category.compliance',
    icon: 'verified',
    color: CATEGORY_COLORS['compliance'],
    types: [
      {
        type: PolicyType.FRAMEWORK_AGREEMENT,
        labelKey: 'policyBuilder.type.frameworkAgreement.label',
        descriptionKey: 'policyBuilder.type.frameworkAgreement.description',
        icon: 'handshake',
        category: 'compliance',
        allowedIn: ['access', 'usage']
      }
    ]
  }
];

/** Fast lookup: PolicyType → metadata. */
export const TYPE_META = new Map<PolicyType, ConstraintTypeOption>(
  CONSTRAINT_CATEGORIES.flatMap(cat => cat.types).map(t => [t.type, t])
);

/**
 * CX-0152 mandatory constraints per role.
 * Usage policies MUST include UsagePurpose + FrameworkAgreement.
 */
const MANDATORY_TYPES: Partial<Record<PolicyRole, Set<PolicyType>>> = {
  usage: new Set([PolicyType.USE_CASE_MEMBERSHIP, PolicyType.FRAMEWORK_AGREEMENT])
};

/** Check whether a constraint type is mandatory (non-removable) for the given role. */
export function isMandatoryConstraint(type: PolicyType, role: PolicyRole | undefined): boolean {
  if (!role) return false;
  return MANDATORY_TYPES[role]?.has(type) ?? false;
}

/** Check whether a constraint has been meaningfully configured (beyond defaults). */
export function isConstraintConfigured(c: Constraint): boolean {
  switch (c.type) {
    case PolicyType.MEMBERSHIP_STATIC:
    case PolicyType.FRAMEWORK_AGREEMENT:
      return true;
    case PolicyType.PARTICIPANT_RESTRICTED:
      return c.bpnNumbers.length > 0;
    case PolicyType.TIME_PERIOD_RESTRICTED:
      return c.count > 0;
    case PolicyType.END_DATE:
      return !!c.endDate;
    case PolicyType.USE_CASE_MEMBERSHIP:
      return !!c.useCaseId;
    case PolicyType.COMPANY_REGION_RESTRICTED:
    case PolicyType.DATACENTER_REGION_RESTRICTED:
      return !!c.region;
    default:
      return false;
  }
}
